import { PrimaryButton } from '@Components/Common/PrimaryButton';
import { Text } from '@Components/Common/Text';
import { useScreenSize } from '@Hooks/useScreenSize';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { ScrollView } from 'react-native';
import { Platform } from 'react-native';
import styled from 'styled-components/native';

import CloseButton from '../../Components/Common/CloseButton';
import TextInput from '../../Components/Common/TextInput';
import OwnableTheme from '../../assets/OwnableTheme';
import {
  StyledFieldContainer,
  StyledInputLabel,
} from './components/SharedStyles';
import useAccountManagementPresenter from './presenter';

import { useAccountManagementStore } from '~stores/account-management';
import { ScreenSize } from '~types/views-types';

const PasswordManagement = React.lazy(
  () => import('./components/PasswordManagement')
);
const AccountDetails = React.lazy(() => import('./components/AccountDetails'));
const SavedAddresses = React.lazy(() => import('./components/SavedAddresses'));
const SavedCards = React.lazy(() => import('./components/SavedCards'));
const Privacy = React.lazy(() => import('./components/Privacy'));
const SideMenu = React.lazy(() => import('./components/SideMenu'));

const RootContainer = styled.View<{
  width: string;
  flexDirection?: 'column' | 'row';
  screenSize?: ScreenSize;
}>`
  max-height: ${({ screenSize }) =>
    screenSize &&
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(screenSize)
      ? '95%'
      : '65%'};
  width: ${({ width }) => width ?? '50%'};
  flex-direction: ${({ flexDirection }) =>
    flexDirection ? flexDirection : 'row'};
  background-color: ${() => OwnableTheme.colors.white};
  border-radius: 15px;
  margin: ${({ screenSize }) =>
    screenSize &&
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(screenSize)
      ? '15px'
      : '0px'};
  padding: ${({ screenSize }) =>
    screenSize &&
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(screenSize)
      ? '0 0 0px 0'
      : '0 40px'};
`;

const ScrollContainer = styled.ScrollView<{
  screenSize?: ScreenSize;
  modalHeight: number;
}>`
  padding: 0 40px;
  width: ${({ screenSize }) =>
    screenSize &&
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(screenSize)
      ? '100%'
      : '85%'};
`;

const PasswordContainer = styled.View`
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 90%;
`;

const PasswordInputContainer = styled.View`
  justify-content: center;
  align-items: center;
  position: relative;
  flex: 1;
`;

const CloseButtonContainer = styled.View<{ screenSize?: ScreenSize }>`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 100;
  height: ${({ screenSize }) =>
    screenSize &&
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(screenSize)
      ? 80
      : 60}px;
`;

const CloseButtonWrapper = styled.View<{
  position: { top: string; right: string };
}>`
  position: absolute;
  right: ${({ position }) => position.right ?? '-50px'};
  top: ${({ position }) => position.top ?? '0px'};
  z-index: 100;
  padding: 5px;
  border-radius: 100px;
  background-color: ${OwnableTheme.colors.black};
`;

type TAnchor =
  | 'AccountDetails'
  | 'PasswordManagement'
  | 'Privacy'
  | 'SavedAddresses'
  | 'SavedCards';

type IMenuItem = {
  menuLabel: string;
  scrollPosition: number;
  anchor: TAnchor;
  isActive?: boolean;
};

type TMenuItems = Record<TAnchor, IMenuItem>;

const AccountManagementPage: FC = () => {
  const [size] = useScreenSize();
  const scrollViewRef = useRef<ScrollView>();
  // @ts-expect-error
  const [sections, setSections] = useState<TMenuItems>({});
  const {
    setCloseModal,
    checkUser,
    checkToken,
    setPasswordCheck,
    handleSubmitPasswordCheck,
    uiAccountModalWidth,
    setUiAccountModalWidth,
  } = useAccountManagementPresenter();
  const { passwordCheckError, uiPasswordShown, passwordCheck, token } =
    useAccountManagementStore();

  // Used to set the size of each section
  const [modalHeight, setModalHeight] = useState<number>(0);
  const [modalWidth, setModalWidth] = useState<number>(0);

  useEffect(() => {
    (async () => {
      await checkUser();
    })();
  }, []);

  useEffect(() => {
    checkToken();
  }, [sections]);

  useEffect(() => {
    setUiAccountModalWidth(size);
  }, [size]);

  const updateSection = ({ anchor, menuLabel, scrollPosition }: IMenuItem) => {
    setSections((_existingSections) => ({
      ..._existingSections,
      [anchor]: {
        anchor,
        menuLabel,
        scrollPosition,
        isActive: false,
      },
    }));
  };

  const setActiveSection = (sectionAnchor: TAnchor) => {
    setSections((_existingSections) => {
      // @ts-expect-error
      const tmp: TMenuItems = {};

      Object.entries(_existingSections).forEach(([key, value]) => {
        tmp[key as TAnchor] = {
          ...value,
          isActive: false,
        };
      });

      return tmp;
    });

    setSections((_existingSections) => ({
      ..._existingSections,
      [sectionAnchor]: {
        ..._existingSections[sectionAnchor],
        isActive: true,
      },
    }));
  };

  const handleScroll = (scrollPosition: number) => {
    switch (true) {
      case scrollPosition >=
        sections.SavedCards?.scrollPosition - modalHeight / 4:
        setActiveSection('SavedCards');
        break;

      case scrollPosition >=
        sections.SavedAddresses?.scrollPosition - modalHeight / 4:
        setActiveSection('SavedAddresses');
        break;

      case scrollPosition >= sections.Privacy?.scrollPosition - modalHeight / 4:
        setActiveSection('Privacy');
        break;

      case scrollPosition >=
        sections.PasswordManagement?.scrollPosition - modalHeight / 4:
        setActiveSection('PasswordManagement');
        break;
      case scrollPosition >= sections.AccountDetails?.scrollPosition:
        setActiveSection('AccountDetails');
        break;
      default: // do nothing
    }
  };

  if (!token?.token || uiPasswordShown) {
    return (
      <RootContainer
        width={uiAccountModalWidth}
        onLayout={({
          nativeEvent: {
            // @ts-expect-error
            layout: { width },
          },
        }) => {
          setModalWidth(width);
        }}
      >
        <PasswordInputContainer>
          <CloseButtonContainer>
            <CloseButton
              iconColor={OwnableTheme.colors.black}
              onPress={() => {
                setCloseModal();
              }}
              size='medium'
            />
          </CloseButtonContainer>
          <PasswordContainer>
            <StyledFieldContainer maxWidth='400px'>
              <StyledInputLabel
                align='center'
                color={OwnableTheme.colors.black}
              >
                Enter your password
              </StyledInputLabel>
              <TextInput
                gutterBottom={16}
                numberOfLines={1}
                onChange={(value) => setPasswordCheck(value)}
                value={passwordCheck}
                fontSizePx={24}
                placeholder='Confirm Password'
                secureTextEntry
                bgColor='mercury'
                color={OwnableTheme.colors.black}
                placeholderColor={OwnableTheme.colors.gray}
              />
            </StyledFieldContainer>
            <StyledFieldContainer maxWidth='400px' align='center'>
              <PrimaryButton
                text='Submit'
                color='black'
                rightMargin={0}
                textColor='white'
                onPress={() => handleSubmitPasswordCheck()}
              />
            </StyledFieldContainer>
            <Text color={OwnableTheme.colors.errorRed}>
              {passwordCheckError ? 'Invalid password, please try again.' : ' '}
            </Text>
          </PasswordContainer>
        </PasswordInputContainer>
      </RootContainer>
    );
  }
  const isMobileScreen = [ScreenSize.mobile, ScreenSize.thinmobile].includes(
    size
  );
  return (
    <RootContainer
      // for some reason putting width calculation directly in presenter
      // does not work
      width={size === ScreenSize.tabletLandscape ? '60%' : uiAccountModalWidth}
      flexDirection={isMobileScreen ? 'column' : 'row'}
      screenSize={size}
      onLayout={({
        nativeEvent: {
          // @ts-expect-error
          layout: { height },
        },
      }) => {
        setModalHeight(height);
      }}
    >
      <CloseButtonWrapper
        position={
          isMobileScreen
            ? { top: '5', right: '5' }
            : { top: '0', right: '-50px' }
        }
      >
        <CloseButton
          iconColor={OwnableTheme.colors.white}
          bgColor={OwnableTheme.colors.black}
          size='xsmall'
          onPress={setCloseModal}
        />
      </CloseButtonWrapper>
      <SideMenu
        sections={sections}
        setActiveSection={setActiveSection}
        scrollViewRef={scrollViewRef}
        modalHeight={modalHeight}
        modalWidth={modalWidth}
      />
      <ScrollContainer
        modalHeight={modalHeight}
        screenSize={size}
        ref={scrollViewRef}
        onScroll={({
          nativeEvent: {
            // @ts-expect-error
            contentOffset: { y },
          },
        }) => {
          handleScroll(y);
        }}
        scrollEventThrottle={Platform.OS === 'web' ? 5 : 5}
      >
        <AccountDetails updateSection={updateSection} size={size} />
        <PasswordManagement updateSection={updateSection} size={size} />
        <Privacy updateSection={updateSection} size={size} />
        <SavedAddresses updateSection={updateSection} size={size} />
        <SavedCards updateSection={updateSection} size={size} />
      </ScrollContainer>
    </RootContainer>
  );
};

export default observer(AccountManagementPage);
